import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { 
  ModelSettingsConfig, 
  ModelType, 
  UserModelSettings, 
  ModelValidationResult,
  MODEL_CAPABILITIES,
  ModelConfiguration,
  SPR_OCR_MODELS,
  isSprOcrModel
} from '../../models/model-settings.interface';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ModelSettingsService {
  private readonly DB_NAME = 'OcrModelSettingsDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'modelSettings';
  private readonly SETTINGS_KEY = 'userModelSettings';

  private db: IDBDatabase | null = null;
  private currentSettingsSubject = new BehaviorSubject<ModelSettingsConfig>(environment.defaultModelSettings);
  public currentSettings$ = this.currentSettingsSubject.asObservable();

  constructor() {
    this.initializeDatabase();
  }

  /**
   * Initialize the database and load settings
   */
  private async initializeDatabase(): Promise<void> {
    try {
      await this.initializeDB();
      await this.loadSettings();
    } catch (error) {
      console.error('Failed to initialize model settings database:', error);
      // Fallback to default settings
      this.currentSettingsSubject.next(environment.defaultModelSettings);
    }
  }

  /**
   * Initialize IndexedDB
   */
  private async initializeDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store for model settings
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Load settings from IndexedDB or use default
   */
  private async loadSettings(): Promise<void> {
    try {
      const savedSettings = await this.getSettingsFromDB();
      if (savedSettings) {
        // Merge saved settings with default settings to ensure new models are included
        const mergedSettings = this.mergeWithDefaults(savedSettings.settings);
        this.currentSettingsSubject.next(mergedSettings);
      } else {
        // Use default settings from environment
        this.currentSettingsSubject.next(environment.defaultModelSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      this.currentSettingsSubject.next(environment.defaultModelSettings);
    }
  }

  /**
   * Merge saved settings with default settings to ensure new models are included
   */
  private isModelDeprecatedInEnv(modelType: ModelType): boolean {
    const envModel = (environment.defaultModelSettings.models as any)[modelType];
    return envModel?.isDeprecated === true;
  }

  private mergeWithDefaults(savedSettings: ModelSettingsConfig): ModelSettingsConfig {
    const defaultSettings = environment.defaultModelSettings;

    // Determine the primary model: use saved value only if it's not deprecated
    const savedPrimary = savedSettings.primaryModel;
    const primaryModel = (!savedPrimary || this.isModelDeprecatedInEnv(savedPrimary as ModelType))
      ? defaultSettings.primaryModel
      : savedPrimary;

    // Start with default settings as base
    const mergedSettings: ModelSettingsConfig = {
      ...defaultSettings,
      primaryModel,
      useTraditionalPipelineOnly: savedSettings.useTraditionalPipelineOnly ?? defaultSettings.useTraditionalPipelineOnly,
      models: { ...defaultSettings.models }
    };

    // Merge model order - keep saved order but add any new models from defaults
    // Remove deprecated models from the saved order
    const savedModelOrder = (savedSettings.modelOrder || [])
      .filter(m => !this.isModelDeprecatedInEnv(m as ModelType));
    const newModels = defaultSettings.modelOrder
      .filter(model => !savedModelOrder.includes(model) && !this.isModelDeprecatedInEnv(model));
    // Append deprecated models at the end (they stay in the data but are hidden in UI)
    const deprecatedModels = defaultSettings.modelOrder
      .filter(model => this.isModelDeprecatedInEnv(model));
    mergedSettings.modelOrder = [...savedModelOrder, ...newModels, ...deprecatedModels];

    // Merge individual model settings - preserve user customizations but
    // ALWAYS respect isDeprecated and enabled:false from environment for deprecated models
    Object.keys(savedSettings.models || {}).forEach(modelType => {
      if (mergedSettings.models[modelType as ModelType]) {
        const envDefaults = mergedSettings.models[modelType as ModelType];
        const isDeprecated = this.isModelDeprecatedInEnv(modelType as ModelType);

        if (isDeprecated) {
          // For deprecated models: keep env defaults, never allow user to re-enable
          mergedSettings.models[modelType as ModelType] = {
            ...envDefaults,
            enabled: false,
            isDeprecated: true
          };
        } else {
          // For active models: merge user customizations on top of env defaults
          mergedSettings.models[modelType as ModelType] = {
            ...envDefaults,
            ...savedSettings.models[modelType as ModelType],
            isDeprecated: false
          };
        }
      }
    });

    console.log('Merged settings with new models:', mergedSettings);
    return mergedSettings;
  }

  /**
   * Get settings from IndexedDB
   */
  private async getSettingsFromDB(): Promise<UserModelSettings | null> {
    if (!this.db) {
      await this.initializeDB();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.get(this.SETTINGS_KEY);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Save settings to IndexedDB
   */
  private async saveSettingsToDB(settings: ModelSettingsConfig): Promise<void> {
    console.log('saveSettingsToDB called');
    
    if (!this.db) {
      console.log('Database not initialized, initializing...');
      await this.initializeDB();
    }

    const userSettings: UserModelSettings = {
      id: this.SETTINGS_KEY,
      settings: settings,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    console.log('Preparing to save user settings:', JSON.stringify(userSettings, null, 2));

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.put(userSettings);

      request.onsuccess = () => {
        console.log('Settings saved to IndexedDB successfully');
        resolve();
      };

      request.onerror = () => {
        console.error('Error saving to IndexedDB:', request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        console.log('IndexedDB transaction completed successfully');
      };

      transaction.onerror = () => {
        console.error('IndexedDB transaction failed:', transaction.error);
        reject(transaction.error);
      };
    });
  }

  /**
   * Get current model settings (returns a deep copy to prevent mutations)
   */
  getCurrentSettings(): ModelSettingsConfig {
    return JSON.parse(JSON.stringify(this.currentSettingsSubject.value));
  }

  /**
   * Update model settings
   */
  async updateSettings(settings: ModelSettingsConfig): Promise<void> {
    try {
      console.log('ModelSettingsService.updateSettings called with:', JSON.stringify(settings, null, 2));
      
      // Validate settings first
      const validation = this.validateSettings(settings);
      if (!validation.isValid) {
        console.error('Settings validation failed:', validation.errors);
        throw new Error(`Invalid settings: ${validation.errors.join(', ')}`);
      }

      console.log('Settings validation passed, saving to IndexedDB...');
      
      // Save to IndexedDB
      await this.saveSettingsToDB(settings);
      console.log('Settings saved to IndexedDB successfully');
      
      // Update current settings
      this.currentSettingsSubject.next(settings);
      console.log('Settings updated in BehaviorSubject');
      
      console.log('Model settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }

  /**
   * Reset to default settings
   */
  async resetToDefaults(): Promise<void> {
    try {
      const defaultSettings = environment.defaultModelSettings;
      await this.updateSettings(defaultSettings);
    } catch (error) {
      console.error('Error resetting to defaults:', error);
      throw error;
    }
  }

  /**
   * Clear all cached settings and reload defaults
   */
  async clearCache(): Promise<void> {
    try {
      if (!this.db) {
        await this.initializeDB();
      }

      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      await store.clear();
      
      // Reload default settings
      this.currentSettingsSubject.next(environment.defaultModelSettings);
      console.log('Settings cache cleared, defaults reloaded');
    } catch (error) {
      console.error('Error clearing cache:', error);
      throw error;
    }
  }

  /**
   * Validate model settings
   */
  validateSettings(settings: ModelSettingsConfig): ModelValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if primary model exists and is enabled
    if (!settings.models[settings.primaryModel]) {
      errors.push(`Primary model ${settings.primaryModel} not found in model configuration`);
    } else if (!settings.models[settings.primaryModel].enabled) {
      warnings.push(`Primary model ${settings.primaryModel} is disabled`);
    }

    // Check model order
    if (!settings.modelOrder || settings.modelOrder.length === 0) {
      errors.push('Model order cannot be empty');
    }

    // Validate each model's settings
    Object.entries(settings.models).forEach(([modelType, config]: [string, ModelConfiguration]) => {
      const capabilities = MODEL_CAPABILITIES[modelType as ModelType];
      if (!capabilities) {
        errors.push(`Unknown model type: ${modelType}`);
        return;
      }

      // Validate API timeout if present
      if ('apiTimeout' in config.settings) {
        const timeout = (config.settings as any).apiTimeout;
        if (timeout < capabilities.apiTimeoutRange.min || timeout > capabilities.apiTimeoutRange.max) {
          errors.push(`${modelType} API timeout must be between ${capabilities.apiTimeoutRange.min} and ${capabilities.apiTimeoutRange.max} seconds`);
        }
      }

      // Validate thinking mode support
      if ('thinkingMode' in config.settings && (config.settings as any).thinkingMode && !capabilities.supportsThinking) {
        warnings.push(`${modelType} does not support thinking mode`);
      }

      // Validate image URL support
      if ('useImageUrl' in config.settings && (config.settings as any).useImageUrl && !capabilities.supportsImageUrl) {
        warnings.push(`${modelType} does not support image URL mode`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Check if user is super admin
   * Priority: environment (static, highest) -> localStorage (session-based, only when env=false)
   */
  isSuperAdmin(): boolean {
    // Environment config takes highest priority.
    // If isSuperAdmin=true in environment.ts, always return true regardless of localStorage.
    if (environment.isSuperAdmin === true) {
      return true;
    }
    // Only check localStorage when environment explicitly disables super admin
    // (allows granting super admin via login credentials at runtime)
    const sessionSuperAdmin = localStorage.getItem('isSuperAdmin');
    if (sessionSuperAdmin !== null) {
      return sessionSuperAdmin === 'true';
    }
    return false;
  }

  /**
   * Get model order for processing
   */
  getProcessingOrder(): ModelType[] {
    const settings = this.getCurrentSettings();
    return settings.modelOrder.filter((modelType: ModelType) => 
      settings.models[modelType]?.enabled
    );
  }

  /**
   * Get primary model
   */
  getPrimaryModel(): ModelType {
    return this.getCurrentSettings().primaryModel;
  }

  /**
   * Update model order (for drag-drop reordering)
   */
  async updateModelOrder(newOrder: ModelType[]): Promise<void> {
    const currentSettings = this.getCurrentSettings();
    const updatedSettings = {
      ...currentSettings,
      modelOrder: newOrder
    };
    await this.updateSettings(updatedSettings);
  }

  /**
   * Toggle model enabled state
   */
  async toggleModelEnabled(modelType: ModelType, enabled: boolean): Promise<void> {
    const currentSettings = this.getCurrentSettings();
    const updatedSettings = {
      ...currentSettings,
      models: {
        ...currentSettings.models,
        [modelType]: {
          ...currentSettings.models[modelType],
          enabled
        }
      }
    };
    await this.updateSettings(updatedSettings);
  }

  /**
   * Update specific model settings
   */
  async updateModelSettings(modelType: ModelType, newSettings: any): Promise<void> {
    const currentSettings = this.getCurrentSettings();
    const updatedSettings = {
      ...currentSettings,
      models: {
        ...currentSettings.models,
        [modelType]: {
          ...currentSettings.models[modelType],
          settings: {
            ...currentSettings.models[modelType].settings,
            ...newSettings
          }
        }
      }
    };
    await this.updateSettings(updatedSettings);
  }

  /**
   * Set primary model
   */
  async setPrimaryModel(modelType: ModelType): Promise<void> {
    const currentSettings = this.getCurrentSettings();
    
    // Ensure the model is enabled
    if (!currentSettings.models[modelType]?.enabled) {
      await this.toggleModelEnabled(modelType, true);
    }

    const updatedSettings = {
      ...currentSettings,
      primaryModel: modelType
    };
    await this.updateSettings(updatedSettings);
  }

  /**
   * Export settings for backend
   */
  exportSettingsForBackend(): any {
    const settings = this.getCurrentSettings();
    
    // If traditional pipeline only, return minimal settings
    if (settings.useTraditionalPipelineOnly) {
      return {
        USE_TRADITIONAL_PIPELINE_ONLY: true,
        PRIMARY_MODEL: 'DOCTR',
        USE_GPT_4O_OCR: false,
        USE_GLM_45V_OCR: false,
        USE_HUGGINGFACE_GLM_45V_OCR: false,
        USE_GROQ_SCOUT_OCR: false,
        USE_GROQ_MAVERICK_OCR: false,
        USE_FIREWORKS_GLM45V_OCR: false,
        USE_QWEN2P5_VL_32B_INSTRUCT_OCR: false
      };
    }
    
    // Convert to backend format for AI models
    const backendSettings: any = {
      USE_TRADITIONAL_PIPELINE_ONLY: false,
      PRIMARY_MODEL: settings.primaryModel
    };
    
    Object.entries(settings.models).forEach(([modelType, config]: [string, ModelConfiguration]) => {
      // Force deprecated models to disabled - never send them as enabled to backend
      const isDeprecated = this.isModelDeprecatedInEnv(modelType as ModelType) || config.isDeprecated === true;

      // Enable/disable flags - use correct variable name for each model
      const useVarName = this.getUseVariableName(modelType as ModelType);
      backendSettings[useVarName] = isDeprecated ? false : config.enabled;

      // Model-specific settings (only for non-deprecated models)
      if (!isDeprecated) {
        Object.entries(config.settings).forEach(([key, value]) => {
          const backendKey = this.convertToBackendKey(modelType as ModelType, key);
          backendSettings[backendKey] = value;
        });
      }
    });

    // Exclude deprecated models from modelOrder sent to backend
    const activeModelOrder = settings.modelOrder
      .filter(m => !this.isModelDeprecatedInEnv(m as ModelType));

    return {
      primaryModel: settings.primaryModel,
      modelOrder: activeModelOrder,
      settings: backendSettings
    };
  }

  /**
   * Get model prefix for backend variables
   */
  private getModelPrefix(modelType: ModelType): string {
    const prefixMap: Record<ModelType, string> = {
      'GPT_4O': 'GPT',
      'GLM_45V': 'GLM45V',
      'HUGGINGFACE_GLM_45V': 'HUGGINGFACE_GLM45V',
      'GROQ_SCOUT': 'GROQ_LLAMA4_SCOUT',
      'GROQ_MAVERICK': 'GROQ_LLAMA4_MAVERICK',
      'FIREWORKS_GLM_45V': 'FIREWORKS_GLM45V',
      'QWEN2P5_VL_32B_INSTRUCT': 'QWEN2P5_VL_32B_INSTRUCT'
    };
    return prefixMap[modelType];
  }

  /**
   * Get the correct USE_xxx environment variable name for each model
   * Some models use _OCR suffix, others use _OCR_SERVICE suffix
   */
  private getUseVariableName(modelType: ModelType): string {
    const variableMap: Record<ModelType, string> = {
      'GPT_4O': 'USE_GPT_OCR',
      'GLM_45V': 'USE_GLM45V_OCR',
      'HUGGINGFACE_GLM_45V': 'USE_HUGGINGFACE_GLM45V_OCR_SERVICE',
      'GROQ_SCOUT': 'USE_GROQ_LLAMA4_SCOUT_OCR',
      'GROQ_MAVERICK': 'USE_GROQ_LLAMA4_MAVERICK_OCR',
      'FIREWORKS_GLM_45V': 'USE_FIREWORKS_GLM45V_OCR',
      'QWEN2P5_VL_32B_INSTRUCT': 'USE_QWEN2P5_VL_32B_INSTRUCT_OCR'
    };
    return variableMap[modelType];
  }

  /**
   * Convert frontend setting key to backend environment variable name
   */
  private convertToBackendKey(modelType: ModelType, settingKey: string): string {
    const prefix = this.getModelPrefix(modelType);
    
    const keyMap: { [key: string]: string } = {
      'thinkingMode': 'THINKING_MODE',
      'optimizeImageSize': 'OPTIMIZE_IMAGE_SIZE',
      'useImageUrl': 'USE_IMAGE_URL',
      'apiTimeout': 'API_TIMEOUT',
      'processingMode': 'PROCESSING_MODE'
    };

    const backendKey = keyMap[settingKey] || settingKey.toUpperCase();
    return `${backendKey}_${prefix}`;
  }

  /**
   * Clear all settings from IndexedDB
   */
  async clearSettings(): Promise<void> {
    if (!this.db) {
      await this.initializeDB();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.delete(this.SETTINGS_KEY);

      request.onsuccess = () => {
        this.currentSettingsSubject.next(environment.defaultModelSettings);
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  isSrcAppWindocDirect(): boolean {
    const srcApp = localStorage.getItem('src_app') || '';
    return srcApp === 'windoc_direct';
  }

  /**
   * Check if Traditional OCR mode is selected
   * When true, AI models are skipped and DocTR pipeline is used directly
   */
  isTraditionalModeSelected(): boolean {
    return this.getCurrentSettings().useTraditionalPipelineOnly === true;
  }

  /**
   * Check if SPR OCR mode is enabled
   * This mode uses specialized SPR-specific AI services and JSON output
   */
  isSprOcrMode(): boolean {
    return environment.isSprOcrEnv === true;
  }

  /**
   * Get available models based on current mode
   * In SPR OCR mode, only SPR-compatible models are returned
   */
  getAvailableModels(): ModelType[] {
    if (this.isSprOcrMode()) {
      return SPR_OCR_MODELS;
    }
    // Standard mode - return all models
    return Object.keys(MODEL_CAPABILITIES) as ModelType[];
  }

  /**
   * Get model order filtered for current mode
   * In SPR OCR mode, only SPR-compatible models are included
   */
  getFilteredModelOrder(): ModelType[] {
    const settings = this.getCurrentSettings();
    if (this.isSprOcrMode()) {
      return settings.modelOrder.filter(model => isSprOcrModel(model));
    }
    return settings.modelOrder;
  }

  /**
   * Get SPR OCR specific default settings
   * Used when isSprOcrEnv is true
   */
  getSprOcrDefaultSettings(): ModelSettingsConfig {
    const baseSettings = environment.defaultModelSettings;
    return {
      ...baseSettings,
      primaryModel: 'GLM_45V' as ModelType,
      modelOrder: SPR_OCR_MODELS,
      models: {
        ...baseSettings.models,
        // Ensure SPR OCR models are enabled
        'GLM_45V': { ...baseSettings.models['GLM_45V'], enabled: true },
        'QWEN2P5_VL_32B_INSTRUCT': { ...baseSettings.models['QWEN2P5_VL_32B_INSTRUCT'], enabled: true },
        'GROQ_SCOUT': { ...baseSettings.models['GROQ_SCOUT'], enabled: true },
        // Disable non-SPR OCR models
        'GPT_4O': { ...baseSettings.models['GPT_4O'], enabled: false },
        'HUGGINGFACE_GLM_45V': { ...baseSettings.models['HUGGINGFACE_GLM_45V'], enabled: false },
        'GROQ_MAVERICK': { ...baseSettings.models['GROQ_MAVERICK'], enabled: false },
        'FIREWORKS_GLM_45V': { ...baseSettings.models['FIREWORKS_GLM_45V'], enabled: false }
      }
    };
  }
}